import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import { isBCHContent, isSpamContent, isSpamAccount } from '../lib/bchFilter';
import PostCard from '../components/PostCard';
import { TrendingUp, Clock, Flame, RefreshCw, Coins, ChevronRight, Mic2, Video as VideoIcon } from 'lucide-react';
import { BCHLogo } from '../components/Layout';
import { usePodcastDirectory } from '../hooks/usePodcast';
import { podcastPath } from '../lib/podcast';
import { useVideoDirectory } from '../hooks/useVideo';
import { videoWatchPath, formatVideoDuration } from '../lib/video';

// ── Podcasts category ─────────────────────────────────────────────
//
// Podcasts are a CATEGORY within the existing discovery surface rather
// than a new top-level destination. This strip is the entry point: a few
// covers plus a link into the full directory.

function PodcastsStrip() {
  const { podcasts } = usePodcastDirectory();
  if (podcasts.length === 0) return null;

  return (
    <div className="border-b border-border px-4 py-3">
      <Link
        to="/podcasts"
        className="flex items-center gap-1.5 text-xs font-bold hover:text-primary transition-colors"
      >
        <Mic2 className="w-3.5 h-3.5 text-primary" />
        BCH Podcasts
        <span className="text-muted-foreground font-normal">({podcasts.length})</span>
        <ChevronRight className="w-3 h-3 text-muted-foreground ml-auto" />
      </Link>
      <div className="flex gap-2.5 mt-2.5 overflow-x-auto scrollbar-hide">
        {podcasts.slice(0, 10).map(show => (
          <Link
            key={show.pubkey}
            to={podcastPath(show.pubkey)}
            className="shrink-0 w-[5.5rem] group"
          >
            {show.image ? (
              <img
                src={show.image}
                alt=""
                loading="lazy"
                className="w-[5.5rem] h-[5.5rem] rounded-xl object-cover border border-border group-hover:border-primary/50 transition-colors"
              />
            ) : (
              <div className="w-[5.5rem] h-[5.5rem] rounded-xl bg-primary/10 flex items-center justify-center border border-border">
                <Mic2 className="w-5 h-5 text-primary/50" />
              </div>
            )}
            <p className="text-[10px] font-medium mt-1 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
              {show.name}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Videos category ───────────────────────────────────────────────
//
// Video is a CATEGORY within the existing discovery surface, alongside
// podcasts — not a competing top-level destination. This strip is the entry
// point: a few thumbnails plus a link into the full video home.
//
// Deliberately thumbnails only. No player is mounted here, so adding this
// strip costs Explore a handful of lazy images and nothing else.

function VideosStrip() {
  const { videos } = useVideoDirectory();
  if (videos.length === 0) return null;

  return (
    <div className="border-b border-border px-4 py-3">
      <Link
        to="/videos"
        className="flex items-center gap-1.5 text-xs font-bold hover:text-primary transition-colors"
      >
        <VideoIcon className="w-3.5 h-3.5 text-primary" />
        BCH Video
        <span className="text-muted-foreground font-normal">({videos.length})</span>
        <ChevronRight className="w-3 h-3 text-muted-foreground ml-auto" />
      </Link>
      <div className="flex gap-2.5 mt-2.5 overflow-x-auto scrollbar-hide">
        {videos.slice(0, 10).map(video => (
          <Link
            key={`${video.pubkey}:${video.id}`}
            to={videoWatchPath(video.pubkey, video.id)}
            className="shrink-0 w-40 group"
          >
            <div className="relative aspect-video rounded-lg overflow-hidden bg-muted border border-border group-hover:border-primary/50 transition-colors">
              {video.thumbnailUrl ? (
                <img
                  src={video.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-emerald-500/5">
                  <VideoIcon className="w-5 h-5 text-primary/40" />
                </div>
              )}
              {video.duration > 0 && (
                <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/80 text-white text-[9px] font-semibold tabular-nums">
                  {formatVideoDuration(video.duration)}
                </span>
              )}
            </div>
            <p className="text-[10px] font-medium mt-1 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
              {video.title}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Explore() {
  const [sort, setSort] = useState<'recent' | 'trending'>('recent');
  const { isMuted } = useAppContext();
  const [since] = useState(() => {
    const now = Math.floor(Date.now() / 1000);
    return Math.floor((now - 3600 * 48) / 3600) * 3600; // round to hour for stable cache keys
  });

  const { events, loading, refetch } = useNostrQuery({
    filters: [
      { kinds: [1, 1068], '#t': ['bch'], limit: 40, since },
      { kinds: [1, 1068], '#t': ['bitcoincash'], limit: 40, since },
    ],
  });

  const filtered = useMemo(() => {
    const seen = new Set<string>();
    return events
      .filter(e => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        if (isMuted(e.pubkey)) return false;
        if (isSpamContent(e.content)) return false;
        if (isSpamAccount(e.pubkey)) return false;
        return isBCHContent(e.content, e.tags);
      })
      .slice(0, 30);
  }, [events]);

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" /> Explore BCH
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSort('recent')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                sort === 'recent' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <Clock className="w-3 h-3" /> Recent
            </button>
            <button
              onClick={() => setSort('trending')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                sort === 'trending' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <Flame className="w-3 h-3" /> Trending
            </button>
            <button onClick={refetch} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <VideosStrip />
      <PodcastsStrip />

      {loading && filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <BCHLogo size={40} />
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading BCH content...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 px-4">
          <BCHLogo size={48} />
          <p className="text-muted-foreground mt-3">No BCH posts found right now.</p>
          <p className="text-xs text-muted-foreground mt-1">Posts with #BCH or #BitcoinCash tags will appear here.</p>
        </div>
      ) : (
        filtered.map(event => (
          <PostCard key={event.id} event={event} />
        ))
      )}
    </div>
  );
}

